import { useEffect, useRef } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { REPORTING_LE_LENSES_PATH } from '~/consts/reportingLeLensApi';

import useCurrentUser from '~/hooks/useCurrentUser';

import { request } from '~/services/common';

import type ApiError from '~/types/ApiError';

import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { encodedJsonBody } from './encodedJsonBody';

export const MAX_PERSONAL_VIEW_NAME_LENGTH = 80;

/** A report-local query string; the receiving report validates its own supported parameters. */
export type PersonalViewState = { version: 1; search: string };

export type PersonalView = {
  id: string;
  name: string;
  ownerEmail?: string;
  state: PersonalViewState;
  reportVersion: number;
  isDefault: boolean;
  deletedAt: string | null;
  reportChanged: boolean;
};

export type PersonalViewsResponse = { views: Array<PersonalView>; reportVersion: number; canManageAll?: boolean };
export type PersonalViewResponse = { view: PersonalView; reportVersion: number };
export type CreatePersonalViewInput = {
  name: string;
  state: PersonalViewState;
  isDefault?: boolean;
  ownerEmail?: string;
};
export type UpdatePersonalViewInput = {
  id: string;
  name?: string;
  state?: PersonalViewState;
  isDefault?: boolean;
  deleted?: boolean;
};

export const personalViewQueryKeys = {
  all: ['reporting', 'le-lens', 'personal-views'] as const,
  list: (email: string, lensId: string) => [...personalViewQueryKeys.all, email, lensId] as const,
};

const personalViewsUrl = (lensId: string, viewId?: string) =>
  resolveReportingLeLensUrl(
    `${REPORTING_LE_LENSES_PATH}/${encodeURIComponent(lensId)}/personal-views${
      viewId ? `/${encodeURIComponent(viewId)}` : ''
    }`,
  );

export function personalViewFailure(error: unknown, fallback: string): string {
  const body = (error as ApiError | undefined)?.responseBody as { message?: string } | undefined;
  return body?.message || (error instanceof Error && error.message) || fallback;
}

function usePersonalViewIdentity() {
  const { isAuthenticated, user } = useCurrentUser();
  return isAuthenticated ? (user?.email?.trim().toLowerCase() ?? '') : '';
}

/** Private views are cached separately for every signed-in account and report. */
export function useGetPersonalViews(lensId: string | undefined) {
  const email = usePersonalViewIdentity();
  return useQuery({
    queryKey: personalViewQueryKeys.list(email, lensId ?? ''),
    enabled: Boolean(email && lensId),
    retry: false,
    queryFn: async ({ signal }): Promise<PersonalViewsResponse> => {
      if (!email || !lensId) throw new Error('Sign in to see your saved views.');
      return requestWithTimeout<PersonalViewsResponse>(personalViewsUrl(lensId), { signal });
    },
  });
}

function usePersonalViewMutation<TInput>(lensId: string, mutate: (input: TInput) => Promise<PersonalViewResponse>) {
  const email = usePersonalViewIdentity();
  const identity = useRef('');
  identity.current = `${email}:${lensId}`;
  useEffect(() => {
    identity.current = `${email}:${lensId}`;
    return () => {
      identity.current = '';
    };
  }, [email, lensId]);
  const client = useQueryClient();
  const queryKey = personalViewQueryKeys.list(email, lensId);
  // TanStack can update a queued mutation's options after an account switch. Carry the
  // initiating identity in its immutable variables instead of trusting a later closure.
  type ScopedInput = { input: TInput; email: string; lensId: string };
  const mutation = useMutation({
    scope: { id: queryKey.join(':') },
    retry: false,
    mutationFn: async ({ input, email: actor, lensId: report }: ScopedInput) => {
      if (!actor || !report || identity.current !== `${actor}:${report}`) {
        throw new Error('Your account changed. Reopen Saved views and try again.');
      }
      return mutate(input);
    },
    onMutate: async ({ email: actor, lensId: report }: ScopedInput) => {
      const initiatingKey = personalViewQueryKeys.list(actor, report);
      await client.cancelQueries({ queryKey: initiatingKey, exact: true });
      return { queryKey: initiatingKey };
    },
    onSuccess: ({ view, reportVersion }, _input, context) => {
      const key = context?.queryKey ?? queryKey;
      client.setQueryData<PersonalViewsResponse>(key, (current) => {
        if (!current) return current;
        const otherViews = current.views.filter((existing) => existing.id !== view.id);
        return {
          reportVersion,
          canManageAll: current.canManageAll,
          views: [
            ...otherViews.map((existing) => ({
              ...existing,
              isDefault:
                view.isDefault && !view.deletedAt && existing.ownerEmail === view.ownerEmail
                  ? false
                  : existing.isDefault,
              reportChanged: existing.reportChanged || existing.reportVersion !== reportVersion,
            })),
            view,
          ],
        };
      });
    },
    // Reconcile uncertain network failures too: a request may have reached the server.
    onSettled: (_result, _error, _input, context) =>
      client.invalidateQueries({ queryKey: context?.queryKey ?? queryKey, exact: true }),
  });
  return {
    ...mutation,
    variables: mutation.variables?.input,
    mutate: (input: TInput) => mutation.mutate({ input, email, lensId }),
    mutateAsync: (input: TInput) => mutation.mutateAsync({ input, email, lensId }),
  };
}

export function useCreatePersonalView(lensId: string) {
  return usePersonalViewMutation(lensId, async (input: CreatePersonalViewInput) => {
    return (await request(personalViewsUrl(lensId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: encodedJsonBody(input),
    })) as PersonalViewResponse;
  });
}

export function useUpdatePersonalView(lensId: string) {
  return usePersonalViewMutation(lensId, async ({ id, ...input }: UpdatePersonalViewInput) => {
    return (await request(personalViewsUrl(lensId, id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: encodedJsonBody(input),
    })) as PersonalViewResponse;
  });
}
